import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataContext from '../../context/DataProvider/DataContext';
import UserContext from '../../context/UserProvider/UserContext';
import {
  Field,
  FieldInput,
  FieldLabel,
  FieldIcon,
  LoginButton,
  BtnText,
  BtnArrow,
  Spinner,
  ErrorAlert,
} from '../../pages/LoginPage/styles';

// ----------------------------------------------------------------------

export default function AuthLoginForm() {
  const { reloadData } = useContext(DataContext);
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const userName = useRef<HTMLInputElement | null>(null);
  const userPassword = useRef<HTMLInputElement | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = useCallback(
    (e: React.SyntheticEvent) => {
      setIsLoading(true);
      setError(false);
      e.preventDefault();
      if (userName.current && userPassword.current) {
        login(userName.current.value, userPassword.current.value)
          .then((isAuthUser) => {
            if (isAuthUser) {
              setIsLoading(false);
              reloadData();
              navigate('/');
            } else {
              setIsLoading(false);
              setError(true);
            }
          })
          .catch(() => {
            setIsLoading(false);
            setError(true);
          });
      }
    },
    [login, reloadData, navigate],
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      {error && (
        <ErrorAlert data-testid="login-error">
          <svg viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span>Benutzername oder Passwort falsch</span>
        </ErrorAlert>
      )}

      <Field>
        <FieldInput
          ref={userName}
          type="text"
          name="user"
          placeholder="Benutzername"
          autoComplete="username"
          required
        />
        <FieldLabel htmlFor="user">Benutzername</FieldLabel>
        <FieldIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </FieldIcon>
      </Field>

      <Field>
        <FieldInput
          ref={userPassword}
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Passwort"
          autoComplete="current-password"
          required
        />
        <FieldLabel htmlFor="password">Passwort</FieldLabel>
        <FieldIcon onClick={() => setShowPassword(!showPassword)}>
          {showPassword ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </FieldIcon>
      </Field>

      <LoginButton type="submit" loading={isLoading || undefined}>
        {isLoading && <Spinner />}
        <BtnText loading={isLoading || undefined}>
          Anmelden
          <BtnArrow
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </BtnArrow>
        </BtnText>
      </LoginButton>
    </form>
  );
}
